import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { getQuote, getSources } from '../lib/api'
import { TagAutocomplete } from '../components/TagAutocomplete'
import type { Quote, Source } from '../lib/api'

export const QuoteEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [quote, setQuote] = useState<Quote | null>(null)
  const [sources, setSources] = useState<Source[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [sourceId, setSourceId] = useState('')
  const [textContent, setTextContent] = useState('')
  const [textStatus, setTextStatus] = useState('ai_generated')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    Promise.all([getQuote(Number(id)), getSources()]).then(([qRes, sRes]) => {
      const q = qRes.data
      setQuote(q)
      setSourceId(String(q.source_id))
      setTextContent(q.text_content ?? '')
      setTextStatus(q.text_status)
      setSelectedTags(q.tags.map((t) => t.name))
      setSources(sRes.data)
    }).catch(() => setError('Zitat konnte nicht geladen werden.')).finally(() => setIsLoading(false))
  }, [id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!sourceId) { setError('Bitte wähle eine Quelle aus.'); return }

    setIsSubmitting(true)
    setError(null)

    const formData = new FormData()
    formData.append('source_id', sourceId)
    formData.append('text_content', textContent)
    formData.append('text_status', textStatus)
    formData.append('tag_names', selectedTags.join(','))

    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`/api/quotes/${id}`, {
        method: 'PUT',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const json = await res.json()
      if (!res.ok) throw json
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      navigate(`/quote/${id}`)
    } catch (err: unknown) {
      const detail = (err as { detail?: unknown })?.detail
      if (Array.isArray(detail)) setError(detail.map((d: { msg?: string }) => d.msg ?? '').join(', '))
      else if (typeof detail === 'string') setError(detail)
      else setError('Fehler beim Speichern.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) return <p>Wird geladen…</p>
  if (error && !quote) return <p>{error}</p>

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Zitat #{id} bearbeiten</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-1">
              Quelle <span className="text-red-500">*</span>
            </label>
            <select
              id="source"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            >
              <option value="">Quelle auswählen…</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}{s.authors?.length > 0 ? ` — ${s.authors.map((a) => a.name).join(', ')}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-1">
              Text
            </label>
            <textarea
              id="text"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={textStatus}
              onChange={(e) => setTextStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              <option value="ai_generated">AI-generiert</option>
              <option value="manual_verified">Manuell verifiziert</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
            <TagAutocomplete selectedTags={selectedTags} onChange={setSelectedTags} disabled={isSubmitting} />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{error}</div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(`/quote/${id}`)}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !sourceId}
              className="flex-1 bg-blue-900 text-white py-2 px-4 rounded-md hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
